/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("SidePanelWidget", ["ModalWidget", "WidgetFactory"], function (context, cls) {
  /**
   * Generic side panel widget. Displays content in a panel attached to a viewport edge.
   * Provides header, content and footer sections.
   * Inherits close button, show/hide, resize and stored-settings logic from ModalWidget.
   * @class SidePanelWidget
   * @memberOf classes
   * @extends classes.ModalWidget
   */
  cls.SidePanelWidget = context.oo.Class(cls.ModalWidget, function ($super) {
    return /** @lends classes.SidePanelWidget.prototype */ {
      __name: "SidePanelWidget",

      /**
       * Panel attachment position.
       * @type {"left"|"right"|"top"|"bottom"}
       */
      _position: "right",

      /**
       * Sign multiplier for the resize delta.
       * "left" and "top" panels: handle is on the trailing edge → drag away = grow (+1).
       * "right" and "bottom" panels: handle is on the leading edge → drag away = grow (-1).
       * @type {number}
       */
      _resizeSign: -1,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        this._isSystemWidget = true;
        if (opts?.position) {
          this._position = opts.position;
          this._resizeSign = this._position === "left" || this._position === "top" ? 1 : -1;
        }
        if (!opts || !opts.storedSettingsKey) {
          const currentApp = gbc.SessionService.getCurrent()?.getCurrentApplication();
          const appId = currentApp?.applicationInfo?.appId ?? -1;
          opts = {
            ...opts,
            storedSettingsKey: {
              appId: appId,
              formName: "_genero_sidepanel_",
              windowName: "_default_" // To override by the specific side panels
            }
          };
        }
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._applyPosition(this._position);
        this.setSizable(true);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        // no layout
      },

      /**
       * @override
       */
      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        if (keyString === "esc") {
          // close the innermost popup first: an open dropdown (menu, combo) consumes this Escape
          if (cls.DropDownWidget.hasAnyVisible()) {
            cls.DropDownWidget.hideAll();
            return true;
          }
          this.hide();
          if (!this.isDestroyed()) {
            this.destroy();
          }
          return true;
        }
        return $super.manageKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Side panels are anchored to a viewport edge — move is disabled.
       * @override
       */
      _initMovable: function () {
        // no-op
      },

      /**
       * @override
       */
      setMovable: function (_movable) {
        // no-op
      },

      /**
       * Set the panel attachment position.
       * @param {"left"|"right"|"top"|"bottom"} position
       */
      setPosition: function (position) {
        if (this._position === position) {
          return;
        }
        this.removeClass("gbc_SidePanelWidget--" + this._position);
        this._position = position;
        this._resizeSign = position === "left" || position === "top" ? 1 : -1;
        this._applyPosition(position);
        this.savePosition();
      },

      /**
       * @returns {"left"|"right"|"top"|"bottom"}
       */
      getPosition: function () {
        return this._position;
      },

      /**
       * @param {string} position
       * @private
       */
      _applyPosition: function (position) {
        this.addClasses("gbc_SidePanelWidget--" + position);
      },

      // ─── Resize ────────────────────────────────────────────────────────────

      /**
       * Returns true when the panel resizes along the horizontal axis (left/right).
       * @returns {boolean}
       * @private
       */
      _isHorizontalResize: function () {
        return this._position === "left" || this._position === "right";
      },

      /**
       * @override
       */
      _updateSizablePosition: function (deltaX, deltaY) {
        if (this._isHorizontalResize()) {
          this._dialogPane.style.width = deltaX + "px";
        } else {
          this._dialogPane.style.height = deltaY + "px";
        }
        this.emit(context.constants.widgetEvents.modalResize);
      },

      /**
       * @override
       */
      _onSizablePointerDown: function (evt) {
        if (!this._canSize) {
          cls.EventHelper.preventCancelableDefault(evt);
          return;
        }

        this.addClasses("sizing");

        const dialogRect = this._dialogPane.getBoundingClientRect();
        const horizontal = this._isHorizontalResize();
        const minSize = Number.parseInt(context.ThemeService.getValue("gbc-SidePanelWidget-min-size"), 10);
        const maxSize = Number.parseInt(context.ThemeService.getValue("gbc-SidePanelWidget-max-size"), 10);

        if (!this._hasBeenSized) {
          this._dialogPane.classList.add("sized");
          this._hasBeenSized = true;
        }

        if (horizontal) {
          this._sizingPositions.size.x = this._sizingPositions.reference.x = dialogRect.width;
          this._sizingPositions.min.x = minSize;
          this._sizingPositions.max.x = Math.min(maxSize, globalThis.innerWidth);
        } else {
          this._sizingPositions.size.y = this._sizingPositions.reference.y = dialogRect.height;
          this._sizingPositions.min.y = minSize;
          this._sizingPositions.max.y = Math.min(maxSize, globalThis.innerHeight);
        }

        this._isSizing = true;
        this._sizingPositions.current.x = evt.pageX;
        this._sizingPositions.current.y = evt.pageY;
        this._sizingPositions.drag.x = evt.pageX;
        this._sizingPositions.drag.y = evt.pageY;

        this._onSizePointerMove = this._onSizablePointerMove.bind(this);
        this._onSizePointerUp = this._onSizablePointerUp.bind(this);

        document.addEventListener("pointermove", this._onSizePointerMove, { passive: false });
        document.addEventListener("pointerup", this._onSizePointerUp, { passive: false });

        evt.preventDefault();
      },

      /**
       * @override
       */
      _onSizablePointerMove: function (evt) {
        if (!this._isSizing) {
          return;
        }

        this._sizingPositions.current.x = evt.pageX;
        this._sizingPositions.current.y = evt.pageY;
        evt.preventDefault();

        const horizontal = this._isHorizontalResize();
        const sign = this._resizeSign;

        if (horizontal) {
          const deltaX = this._sizingPositions.reference.x + (this._sizingPositions.current.x - this._sizingPositions.drag.x) * sign;
          this._sizingPositions.size.x = Math.min(Math.max(deltaX, this._sizingPositions.min.x), this._sizingPositions.max.x);
          this._updateSizablePosition(this._sizingPositions.size.x, 0);
        } else {
          const deltaY = this._sizingPositions.reference.y + (this._sizingPositions.current.y - this._sizingPositions.drag.y) * sign;
          this._sizingPositions.size.y = Math.min(Math.max(deltaY, this._sizingPositions.min.y), this._sizingPositions.max.y);
          this._updateSizablePosition(0, this._sizingPositions.size.y);
        }
      },

      /**
       * @override
       */
      _onSizablePointerUp: function (_evt) {
        this.removeClass("sizing");

        if (this._isHorizontalResize()) {
          this._sizingPositions.reference.x = this._sizingPositions.size.x;
        } else {
          this._sizingPositions.reference.y = this._sizingPositions.size.y;
        }

        this._isSizing = false;
        document.removeEventListener("pointermove", this._onSizePointerMove);
        document.removeEventListener("pointerup", this._onSizePointerUp);

        this.emit(context.constants.widgetEvents.modalResize);
        this.saveDimension();
      },

      /**
       * Unlike other system widgets, a side panel persists its user-resized size
       * @override
       */
      saveDimension: function () {
        if (this._forceDefaultSettings) {
          return;
        }
        const { appId, formName, windowName } = this._storedSettingsKey;
        gbc.StoredSettingsService.saveModalSettings(appId, formName, windowName, {
          dimension: this._sizingPositions
        });
      },

      /**
       * @override
       */
      restoreStoredDimension: function () {
        const { appId, formName, windowName } = this._storedSettingsKey;
        const stored = gbc.StoredSettingsService.loadModalSettings(appId, formName, windowName, "dimension");

        if (!stored || !Object.keys(stored).length) {
          return;
        }
        this._sizingPositions = stored;

        if (this._isHorizontalResize() && this._sizingPositions.size.x) {
          this._dialogPane.style.width = this._sizingPositions.size.x + "px";
          this._hasBeenSized = true;
          this.emit(context.constants.widgetEvents.modalResize);
        } else if (!this._isHorizontalResize() && this._sizingPositions.size.y) {
          this._dialogPane.style.height = this._sizingPositions.size.y + "px";
          this._hasBeenSized = true;
          this.emit(context.constants.widgetEvents.modalResize);
        }
      },

      /** @override */
      savePosition: function () {
        const { appId, formName, windowName } = this._storedSettingsKey;
        gbc.StoredSettingsService.saveModalSettings(appId, formName, windowName, {
          position: { edge: this._position }
        });
      },

      /** @override */
      restorePosition: function () {
        const { appId, formName, windowName } = this._storedSettingsKey;
        const stored = gbc.StoredSettingsService.loadModalSettings(appId, formName, windowName, "position");
        if (stored?.edge) {
          this.setPosition(stored.edge);
        }
      },

      /** @override */
      resetPosition: function () {
        const { appId, formName, windowName } = this._storedSettingsKey;
        gbc.StoredSettingsService.saveModalSettings(appId, formName, windowName, {
          position: {}
        });
        this.setPosition("right");
      }
    };
  });
});
